import React from 'react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { User } from 'lucide-react';
import { signInWithEmail, signInWithGoogle, sendPasswordResetEmail, sendMagicLink } from '../../../services/auth';
import { useToast } from '../../../store/ToastContext';

const MODEL_USERS = [
    { name: 'Alice Johnson', email: 'alice@example.com' },
    { name: 'Bob Smith', email: 'bob@example.com' },
    { name: 'Charlie Brown', email: 'charlie@example.com' },
    { name: 'Diana Prince', email: 'diana@example.com' },
];

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    email: string;
    setEmail: (val: string) => void;
    password: string;
    setPassword: (val: string) => void;
    isSubmitting: boolean;
    onSubmit: (e: React.SyntheticEvent<HTMLFormElement>) => void;
    onSignUp?: (e: React.SyntheticEvent<HTMLFormElement>) => void;
    initialMode?: 'login' | 'signup';
}

export const LoginModal: React.FC<LoginModalProps> = ({
    isOpen,
    onClose,
    email, setEmail,
    password, setPassword,
    isSubmitting,
    onSubmit,
    onSignUp,
    initialMode = 'login'
}) => {
    const [mode, setMode] = React.useState<'login' | 'signup'>(initialMode);
    const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
    const { showToast } = useToast();
    const [isMagicLinkSending, setIsMagicLinkSending] = React.useState(false);
    const [isResetSending, setIsResetSending] = React.useState(false);

    // Reset mode when modal opens
    React.useEffect(() => {
        if (isOpen) {
            queueMicrotask(() => setMode(initialMode));
        }
    }, [isOpen, initialMode]);

    const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
        if (mode === 'login') {
            onSubmit(e);
        } else {
            if (onSignUp) onSignUp(e);
        }
    };

    const handleModelUserLogin = async (mockEmail: string) => {
        setEmail(mockEmail);
        setPassword('password123');
        try {
            await signInWithEmail(mockEmail, 'password123');
            onClose();
        } catch (err) {
            console.error('Failed to log in as model user:', err);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            await signInWithGoogle();
            onClose();
        } catch (err) {
            console.error('Failed to sign in with Google:', err);
        }
    };

    const handleSendMagicLink = async () => {
        if (!email.trim()) {
            showToast('Please enter your email first.', 'error');
            return;
        }
        setIsMagicLinkSending(true);
        try {
            const redirectUrl = window.location.origin + '/auth/magic-link';
            await sendMagicLink(email, redirectUrl);
            localStorage.setItem('evenly_magic_link_email', email);
            showToast('Magic link sent! Check your inbox.', 'success');
            onClose();
        } catch (err) {
            console.error('Failed to send magic link:', err);
            showToast('Failed to send magic link.', 'error');
        } finally {
            setIsMagicLinkSending(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email.trim()) {
            showToast('Please enter your email first.', 'error');
            return;
        }
        setIsResetSending(true);
        try {
            await sendPasswordResetEmail(email);
            showToast('Password reset email sent! Check your inbox.', 'success');
        } catch (err) {
            console.error('Failed to send password reset:', err);
            showToast('Failed to send password reset.', 'error');
        } finally {
            setIsResetSending(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={mode === 'login' ? 'Login' : 'Create Account'}
        >
            {useEmulators && (
                <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 dark:bg-amber-950/20">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                        <User size={14} /> Quick Login as Model User (Emulator)
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        {MODEL_USERS.map((u) => (
                            <button
                                key={u.email}
                                type="button"
                                onClick={() => handleModelUserLogin(u.email)}
                                className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-amber-500 hover:text-amber-600 dark:hover:text-amber-400 transition-colors flex items-center justify-between shadow-sm"
                            >
                                <span>{u.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex w-full mb-4 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                <button
                    type="button"
                    onClick={() => { setMode('login'); }}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${mode === 'login'
                        ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        }`}
                >
                    Login
                </button>
                <button
                    type="button"
                    onClick={() => { setMode('signup'); }}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${mode === 'signup'
                        ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        }`}
                >
                    Sign Up
                </button>
            </div>

            <div className="mb-4">
                <Button
                    type="button"
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={handleGoogleSignIn}
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                </Button>
            </div>

            <div className="relative mb-4 flex items-center justify-center">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
                </div>
                <div className="relative bg-white dark:bg-bg-card px-2 text-[10px] font-bold text-slate-400 tracking-wider">
                    OR CONTINUE WITH EMAIL
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mb-4">
                <p className="text-xs text-text-muted">
                    {mode === 'login'
                        ? 'Log in to an existing account.'
                        : 'Create a new account to sync your data.'}
                </p>
                <Input
                    id="email"
                    label="Email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); }}
                />
                <div className="space-y-1">
                    <Input
                        id="password"
                        label="Password"
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); }}
                    />
                    {mode === 'login' && (
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleForgotPassword}
                                disabled={isResetSending}
                                className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors font-medium"
                            >
                                {isResetSending ? 'Sending reset link...' : 'Forgot Password?'}
                            </button>
                        </div>
                    )}
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting
                        ? (mode === 'login' ? 'Logging in...' : 'Creating Account...')
                        : (mode === 'login' ? 'Login' : 'Create Account')}
                </Button>
            </form>

            <div className="relative mb-2 mt-6 flex flex-col items-center justify-center space-y-3">
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Prefer not to use a password?</p>
                <Button
                    type="button"
                    variant="outline"
                    className="w-full flex items-center justify-center border-primary-200 text-primary-700 hover:bg-primary-50 dark:border-primary-900 dark:text-primary-400 dark:hover:bg-primary-950/30"
                    onClick={handleSendMagicLink}
                    disabled={isMagicLinkSending}
                >
                    {isMagicLinkSending 
                        ? 'Sending...' 
                        : (mode === 'login' ? 'Log in with Magic Link' : 'Sign up with Magic Link')}
                </Button>
            </div>
        </Modal>
    );
};

